import { ConditionWrapper, Divider, Flex } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { memo } from 'react';
import bemFactory from 'react-bem-factory';
import {
    CurrencyDisclaimer,
    DifferenceWarning,
    SummaryLineValues,
    SummarySection,
    WarningTooltip,
} from 'shared/components';
import { SummaryLineTotal } from 'shared/components';

import { QBooksTaxSwitcher } from '../QBooksTaxSwitcher/QBooksTaxSwitcher';
import { useQBooksRequestItemsSummary } from './QBooksRequestItemsSummary.hooks';
import { messages } from './QBooksRequestItemsSummary.messages';
import { ReorderableContent } from './QBooksRequestItemsSummary.styles';

const qa = bemFactory.qa('line-items-section');

export const QBooksRequestItemsSummary = memo(() => {
    const {
        request,
        summary,
        shipping,
        taxComponents,
        ocrData,
        discount,
        discountType,
        totalTax,
        applyTaxAfterDiscount,
        isUSCompany,
        isSalesInvoice,
        showTotalWarning,
        showTaxTotalWarning,
        showSubtotalWarning,
    } = useQBooksRequestItemsSummary();

    const { currency, details } = request;

    return (
        <SummarySection>
            <CurrencyDisclaimer />

            {isSalesInvoice && (
                <Flex width='100%' justifyContent='end'>
                    <QBooksTaxSwitcher />
                </Flex>
            )}

            <Flex direction='column' spacing='4' width={420} justifyContent='end'>
                <WarningTooltip
                    open={showSubtotalWarning}
                    activator={
                        <SummaryLineValues
                            qa={qa('subtotal-footer-line')}
                            warning={showSubtotalWarning}
                            label={messages.subtotalText({ currency })}
                            value1={intl.formatNumber(summary.subtotalAmount, 2)}
                        />
                    }
                >
                    <DifferenceWarning
                        value1={summary.subtotalAmount}
                        value2={ocrData?.document?.subTotal}
                        label1={messages.differentSubtotalWarningLabel1}
                        label2={messages.differentSubtotalWarningLabel2}
                        currency={currency}
                        description={messages.differentSubtotalWarningDescription}
                    />
                </WarningTooltip>

                <ReorderableContent
                    direction='column'
                    spacing='0'
                    alignItems='end'
                    $ordered={!isUSCompany}
                    $applyTaxAfterDiscount={applyTaxAfterDiscount}
                >
                    {isSalesInvoice && (
                        <>
                            {!!summary.discountAmount && (
                                <SummaryLineValues
                                    label={
                                        discountType === domain.QBooksDiscountType.Percent
                                            ? messages.discountTextPercent({ discount })
                                            : messages.discountText
                                    }
                                    value1={intl.formatNumber(-summary.discountAmount, 2)}
                                />
                            )}

                            {!!shipping && (
                                <SummaryLineValues
                                    label={messages.shippingText}
                                    value1={intl.formatNumber(shipping, 2)}
                                />
                            )}

                            {/* TODO: AM-16342 SalesInvoice: Implement after the backend is ready */}
                            {/* <LineItemsSummaryRow */}
                            {/*    label={messages.taxOnShippingText} */}
                            {/*    value={intl.formatNumber(0, 2)} */}
                            {/* /> */}
                        </>
                    )}

                    {taxComponents.map(({ taxPercent, taxAmount, taxableAmount }, index) => (
                        <ConditionWrapper
                            condition={index === taxComponents.length - 1}
                            key={index}
                            wrapperIf={(children) => (
                                <WarningTooltip open={showTaxTotalWarning} activator={children}>
                                    <DifferenceWarning
                                        value1={totalTax}
                                        value2={ocrData?.document?.totalTax}
                                        label1={messages.differentTaxTotalWarningLabel1}
                                        label2={messages.differentTaxTotalWarningLabel2}
                                        currency={currency}
                                        description={messages.differentTaxTotalWarningDescription}
                                    />
                                </WarningTooltip>
                            )}
                        >
                            <SummaryLineValues
                                qa={qa('tax-component-footer-line')}
                                warning={index === taxComponents.length - 1 && showTaxTotalWarning}
                                label={
                                    details.lineAmountType === domain.LineAmountType.TaxExclusive || isUSCompany
                                        ? messages.taxLineTextExclusive({
                                              percent: taxPercent,
                                              taxableAmount: intl.formatNumber(taxableAmount, 2),
                                          })
                                        : messages.taxLineTextInclusive({
                                              percent: taxPercent,
                                              taxableAmount: intl.formatNumber(taxableAmount, 2),
                                          })
                                }
                                value1={intl.formatNumber(taxAmount, 2)}
                            />
                        </ConditionWrapper>
                    ))}
                </ReorderableContent>

                <Divider color='midnight80' spacing='8 0' />

                {summary.nonDefaultCurrency && (
                    <SummaryLineTotal
                        label={messages.totalText({ currency: summary.defaultCurrency })}
                        value1={intl.formatNumber(summary.totalInDefaultCurrency, 2)}
                        data-qa={qa('total-default-currency-footer-line')}
                    />
                )}

                <WarningTooltip
                    open={showTotalWarning}
                    activator={
                        <SummaryLineTotal
                            warning={showTotalWarning}
                            label={messages.totalText({ currency })}
                            value1={intl.formatNumber(summary.totalAmount, 2)}
                            data-qa={qa('total-footer-line')}
                        />
                    }
                >
                    <DifferenceWarning
                        value1={summary.totalAmount}
                        value2={ocrData?.document?.total}
                        label1={messages.differentTotalWarningLabel1}
                        label2={messages.differentTotalWarningLabel2}
                        currency={currency}
                        description={messages.differentTotalWarningDescription}
                    />
                </WarningTooltip>
            </Flex>
        </SummarySection>
    );
});

QBooksRequestItemsSummary.displayName = 'QBooksRequestItemsSummary';
